// @ts-nocheck
/**
 * アドベンチャーモード用スタイル定義
 */

// ========================================
// 共通カラー定義
// ========================================

export const COLORS = {
  // 背景
  bgPrimary: '#1a1a2e',
  bgSecondary: '#16213e',
  bgTertiary: '#0f0f23',
  bgCard: 'linear-gradient(135deg, #1a1a3a 0%, #2a2a4a 100%)',
  bgHover: 'rgba(107, 76, 230, 0.2)',
  bgSelected: 'rgba(107, 76, 230, 0.4)',
  bgLocked: 'rgba(50, 50, 70, 0.6)',

  // ボーダー
  borderPrimary: 'rgba(107, 76, 230, 0.5)',
  borderSecondary: 'rgba(255, 255, 255, 0.1)',
  borderHighlight: '#6b4ce6',
  borderLocked: 'rgba(100, 100, 100, 0.5)',

  // テキスト
  textPrimary: '#e0e0e0',
  textSecondary: '#a0a0a0',
  textGold: '#ffd700',
  textSuccess: '#4caf50',
  textDanger: '#ff6b6b',
  textLocked: '#666666',

  // アクセント
  accent: '#6b4ce6',
  accentLight: '#9d4ce6',

  // ステータス
  statusCleared: '#4caf50',
  statusUnlocked: '#ffd700',
  statusLocked: '#666666',

  // 難易度
  tierS: '#ff6b6b',
  tierA: '#ff9800',
  tierB: '#ffd700',
  tierC: '#4caf50',
  tierD: '#64b5f6',
};

// ========================================
// 画面レイアウト
// ========================================

export const screenStyles = {
  container: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${COLORS.bgPrimary} 0%, ${COLORS.bgSecondary} 100%)`,
    color: COLORS.textPrimary,
    fontFamily: "'Noto Sans JP', sans-serif",
    display: 'flex',
    flexDirection: 'column' as const,
  },

  containerMobile: {
    padding: '8px',
    paddingTop: '64px',
    paddingBottom: '16px',
  },

  containerDesktop: {
    padding: '20px 24px',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },

  headerMobile: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'rgba(26, 26, 46, 0.98)',
    borderBottom: `2px solid ${COLORS.accent}`,
    boxShadow: `0 4px 20px rgba(107, 76, 230, 0.3)`,
    zIndex: 100,
  },

  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: COLORS.textGold,
    margin: 0,
    textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
  },

  titleMobile: {
    fontSize: '20px',
  },

  backButton: {
    padding: '8px 16px',
    background: 'transparent',
    border: `1px solid ${COLORS.borderPrimary}`,
    borderRadius: '8px',
    color: COLORS.textPrimary,
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
};

// ========================================
// 章タブ
// ========================================

export const chapterTabStyles = {
  tabContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
    overflowX: 'auto' as const,
    paddingBottom: '8px',
    scrollbarWidth: 'thin' as const,
  },

  tab: {
    padding: '10px 16px',
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.borderSecondary}`,
    borderRadius: '8px 8px 0 0',
    color: COLORS.textSecondary,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s ease',
    minWidth: '80px',
    textAlign: 'center' as const,
  },

  tabActive: {
    background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentLight} 100%)`,
    color: '#fff',
    border: `1px solid ${COLORS.borderHighlight}`,
    boxShadow: `0 0 15px rgba(107, 76, 230, 0.5)`,
  },

  tabLocked: {
    opacity: 0.5,
    cursor: 'not-allowed',
    background: COLORS.bgLocked,
  },

  tabProgress: {
    fontSize: '10px',
    marginTop: '4px',
    opacity: 0.8,
  },
};

// ========================================
// NPCカード
// ========================================

export const npcCardStyles = {
  card: {
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.borderSecondary}`,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  cardHover: {
    background: COLORS.bgHover,
    borderColor: COLORS.borderHighlight,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 20px rgba(107, 76, 230, 0.3)`,
  },

  cardCleared: {
    borderColor: COLORS.statusCleared,
    boxShadow: `0 0 10px rgba(76, 175, 80, 0.3)`,
  },

  cardUnlocked: {
    borderColor: COLORS.statusUnlocked,
  },

  cardLocked: {
    opacity: 0.6,
    cursor: 'not-allowed',
    background: COLORS.bgLocked,
    borderColor: COLORS.borderLocked,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },

  portrait: {
    fontSize: '32px',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '50%',
  },

  nameSection: {
    flex: 1,
  },

  name: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: COLORS.textPrimary,
    margin: 0,
  },

  deck: {
    fontSize: '13px',
    color: COLORS.textSecondary,
    marginTop: '2px',
  },

  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
  },

  statusCleared: {
    background: 'rgba(76, 175, 80, 0.2)',
    color: COLORS.statusCleared,
  },

  statusUnlocked: {
    background: 'rgba(255, 215, 0, 0.2)',
    color: COLORS.statusUnlocked,
  },

  statusLocked: {
    background: 'rgba(100, 100, 100, 0.2)',
    color: COLORS.statusLocked,
  },

  info: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },

  difficulty: {
    fontSize: '14px',
    color: COLORS.textGold,
  },

  record: {
    fontSize: '13px',
    color: COLORS.textSecondary,
  },

  unlockRequirement: {
    fontSize: '12px',
    color: COLORS.textLocked,
    fontStyle: 'italic',
    marginTop: '8px',
  },
};

// ========================================
// 章ヘッダー
// ========================================

export const chapterHeaderStyles = {
  container: {
    background: 'rgba(107, 76, 230, 0.1)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: `1px solid ${COLORS.borderPrimary}`,
  },

  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: COLORS.textGold,
    margin: 0,
    marginBottom: '4px',
  },

  description: {
    fontSize: '14px',
    color: COLORS.textSecondary,
    margin: 0,
  },

  progress: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  progressBar: {
    flex: 1,
    height: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    overflow: 'hidden' as const,
  },

  progressFill: {
    height: '100%',
    background: `linear-gradient(90deg, ${COLORS.accent} 0%, ${COLORS.statusSuccess} 100%)`,
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },

  progressText: {
    fontSize: '13px',
    color: COLORS.textSecondary,
    minWidth: '50px',
    textAlign: 'right' as const,
  },
};

// ========================================
// 詳細モーダル
// ========================================

export const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },

  content: {
    background: COLORS.bgPrimary,
    borderRadius: '16px',
    border: `2px solid ${COLORS.borderHighlight}`,
    boxShadow: `0 0 30px rgba(107, 76, 230, 0.5)`,
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto' as const,
  },

  header: {
    padding: '20px',
    borderBottom: `1px solid ${COLORS.borderSecondary}`,
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },

  portrait: {
    fontSize: '48px',
    width: '72px',
    height: '72px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '50%',
  },

  name: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: COLORS.textGold,
    margin: 0,
  },

  body: {
    padding: '20px',
  },

  quote: {
    fontSize: '16px',
    color: COLORS.textPrimary,
    fontStyle: 'italic',
    padding: '16px',
    background: 'rgba(107, 76, 230, 0.1)',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center' as const,
  },

  section: {
    marginBottom: '16px',
  },

  sectionTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: COLORS.textSecondary,
    marginBottom: '8px',
  },

  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },

  infoItem: {
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
  },

  infoLabel: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    marginBottom: '4px',
  },

  infoValue: {
    fontSize: '16px',
    color: COLORS.textPrimary,
    fontWeight: 'bold',
  },

  rewardList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },

  rewardItem: {
    padding: '8px 12px',
    background: 'rgba(255, 215, 0, 0.1)',
    borderRadius: '6px',
    marginBottom: '6px',
    fontSize: '14px',
    color: COLORS.textGold,
  },

  footer: {
    padding: '20px',
    borderTop: `1px solid ${COLORS.borderSecondary}`,
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },

  button: {
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
  },

  buttonPrimary: {
    background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentLight} 100%)`,
    color: '#fff',
    boxShadow: `0 4px 15px rgba(107, 76, 230, 0.4)`,
  },

  buttonSecondary: {
    background: 'transparent',
    border: `1px solid ${COLORS.borderPrimary}`,
    color: COLORS.textPrimary,
  },

  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  closeButton: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    background: 'transparent',
    border: 'none',
    color: COLORS.textSecondary,
    fontSize: '24px',
    cursor: 'pointer',
  },
};

// ========================================
// 結果モーダル
// ========================================

export const resultModalStyles = {
  ...modalStyles,

  resultTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
    marginBottom: '20px',
  },

  resultWin: {
    color: COLORS.textGold,
    textShadow: '0 0 20px rgba(255, 215, 0, 0.5)',
  },

  resultLose: {
    color: COLORS.textDanger,
    textShadow: '0 0 20px rgba(255, 107, 107, 0.5)',
  },

  rewardSection: {
    background: 'rgba(255, 215, 0, 0.1)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
  },

  rewardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: COLORS.textGold,
    marginBottom: '12px',
    textAlign: 'center' as const,
  },

  newUnlock: {
    background: 'rgba(76, 175, 80, 0.1)',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '12px',
    textAlign: 'center' as const,
    color: COLORS.statusCleared,
  },

  buttonGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
};

// ========================================
// 難易度カラー
// ========================================

export const getTierColor = (tier: string): string => {
  const colors: Record<string, string> = {
    S: COLORS.tierS,
    A: COLORS.tierA,
    B: COLORS.tierB,
    C: COLORS.tierC,
    D: COLORS.tierD,
  };
  return colors[tier] || COLORS.textSecondary;
};

// ========================================
// ステータスアイコン
// ========================================

export const STATUS_ICONS = {
  cleared: '✅',
  unlocked: '🔓',
  locked: '🔒',
};
